import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import MatchCard from "@/components/feature/MatchCard";
import { matches } from "@/mocks/matches";
import { seasons } from "@/mocks/seasons";
import { teams } from "@/mocks/teams";

const PAGE_SIZE = 6;
const statusOptions = ["全部状态", "进行中", "未开始", "已结束"];

export default function CategorySeason() {
  const [seasonId, setSeasonId] = useState("2025-26");
  const [stage, setStage] = useState("全部阶段");
  const [status, setStatus] = useState("全部状态");
  const [page, setPage] = useState(1);

  const currentSeason = seasons.find((s) => s.id === seasonId) ?? seasons[0];
  const stageOptions = ["全部阶段", ...currentSeason.stages.map((s) => s.name)];

  const filtered = useMemo(() => {
    return matches.filter((m) => {
      if (m.season !== seasonId) return false;
      if (stage !== "全部阶段" && m.stage !== stage) return false;
      if (status === "进行中" && m.status !== "live") return false;
      if (status === "未开始" && m.status !== "upcoming") return false;
      if (status === "已结束" && m.status !== "finished") return false;
      return true;
    });
  }, [seasonId, stage, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const changeSeason = (id: string) => {
    setSeasonId(id);
    setStage("全部阶段");
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        eyebrow="SEASON & STAGE"
        title="赛事分类 · 赛季与阶段"
        description="按赛季或比赛阶段浏览欧冠赛事，查看对阵、时间与比赛状态，快速定位你关注的比赛。"
        crumbs={[{ label: "首页", to: "/" }, { label: "赛事分类" }]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
          选择赛季
        </h2>
        <div className="flex flex-wrap gap-2 md:gap-3">
          {seasons.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => changeSeason(s.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                s.id === seasonId
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
              {currentSeason.name}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                currentSeason.label === "进行中"
                  ? "bg-accent-100 text-accent-900"
                  : "bg-secondary-100 text-secondary-900"
              }`}
            >
              {currentSeason.label}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-foreground-600">{currentSeason.description}</p>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <span className="text-foreground-500">
              总场次：<strong className="text-foreground-900">{currentSeason.matchCount}</strong>
            </span>
            <span className="text-foreground-500">
              冠军：<strong className="text-foreground-900">{currentSeason.champion}</strong>
            </span>
          </div>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-700 mr-1">比赛阶段</span>
            {stageOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStage(s);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  s === stage
                    ? "bg-secondary-100 text-secondary-900 ring-1 ring-secondary-300"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <span className="text-sm font-medium text-foreground-700 mr-1">比赛状态</span>
            {statusOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStatus(s);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  s === status
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {paged.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 py-16 text-center text-sm text-foreground-500">
              该赛季暂无赛事
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {paged.map((m) => (
                <MatchCard key={m.id} match={m} />
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i + 1)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                  safePage === i + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </div>
        )}

        <div className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
            按球队浏览
          </h2>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {teams.map((t) => (
              <Link
                key={t.id}
                to={`/team/${t.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 pl-1.5 pr-4 py-1.5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center overflow-hidden p-0.5">
                  <img
                    src={t.crest}
                    alt={`${t.name}队徽`}
                    title={`${t.name} 欧冠球队`}
                    className="w-full h-full object-contain"
                  />
                </span>
                {t.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}